import React from 'react';
import { ArrowDropDown, CloseRounded } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { IMask } from 'react-imask';

import { timezones } from '../../constants/timezones';
import { InputCheckbox, InputSelect, StyledTextField } from './Fields/fields';
import MaskedInput from './Fields/MaskedInput';
import usstates from '../../constants/usstates';

const phoneMask = IMask.createMask({
  mask: '000-000-0000',
});

const zipMask = IMask.createMask({
  mask: ['00000-0000', '00000'],
});

// no-dd-sa:javascript-code-style/max-params
const getField = (classes, item, data, formErrors, handleChange, onBlur) => {
  let label = (
    <span>
      {item.label}
      <i className={classes.subLabel}> {item.subLabel}</i>
    </span>
  );
  let component = null;
  const type = item.type ? item.type : 'text';

  if (!!item.hidden) {
    return null;
  }

  switch (type) {
    case 'text':
    case 'email':
    case 'password':
      component = (
        <StyledTextField
          key={`${item.id}-input`}
          type={type}
          value={data[item.id] ?? ''}
          autoFocus={item.autoFocus}
          id={item.id}
          name={item.id}
          disabled={'disabled' in item ? item.disabled : false}
          required={!('notRequired' in item) ? true : false}
          fullWidth
          className={!!classes[item.id] ? classes[item.id] : ''}
          error={!!formErrors[item.id]}
          helperText={formErrors[item.id]}
          variant={!!item.variant ? item.variant : 'outlined'}
          onBlur={onBlur}
          hidden={!!item.hidden}
        />
      );
      break;
    case 'checkbox':
      label = '';
      component = (
        <InputCheckbox
          key={`${item.id}-checkbox`}
          label={`${item.label}${item.subLabel ? ` ${item.subLabel}` : ''}`}
          type={type}
          value={item.id in data ? data[item.id] : false}
          autoFocus={item.autoFocus}
          id={item.id}
          name={item.id}
          disabled={'disabled' in item ? item.disabled : false}
          required={!('notRequired' in item) ? true : false}
          fullWidth
          className={!!classes[item.id] ? classes[item.id] : ''}
          error={!!formErrors[item.id]}
          helperText={formErrors[item.id]}
          variant="outlined"
          labelPlacement={'labelPlacement' in item ? item.labelPlacement : ''}
          onBlur={onBlur}
          hidden={!!item.hidden}
        />
      );
      break;
    case 'select':
    case 'timeZoneSelect':
    case 'stateSelect':
      // no-dd-sa:javascript-best-practices/no-case-declarations
      let selectData = [];
      if (type === 'timeZoneSelect') {
        selectData = timezones.map((option) => ({
          key: option.value,
          value: option.value,
          label: option.text,
        }));
      } else if (type === 'stateSelect') {
        selectData = usstates.map((option) => ({
          key: option.abbreviation,
          value: option.abbreviation,
          label: option.name,
        }));
        selectData.push({ key: '', value: '', label: ' ' });
      } else if (type === 'select') {
        selectData = item.selectOptions;
      }

      component = (
        <InputSelect
          classes={classes}
          key={`${item.id}-select`}
          value={item.id in data && data[item.id] ? data[item.id] : ''}
          autoFocus={item.autoFocus}
          id={item.id}
          name={item.id}
          disabled={'disabled' in item ? item.disabled : false}
          required={!('notRequired' in item) ? true : false}
          fullWidth
          className={!!classes[item.id] ? classes[item.id] : ''}
          error={!!formErrors[item.id]}
          helperText={formErrors[item.id]}
          variant={!!item.variant ? item.variant : 'outlined'}
          data={selectData}
          onChange={handleChange}
          onBlur={onBlur}
          slotProps={{
            input: {
              endAdornment: item.id in data && data[item.id] !== '' && (
                <IconButton
                  size="small"
                  variant="plain"
                  color="primary"
                  onMouseDown={(event) => {
                    // stops the popup from appearing when this button is clicked
                    event.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.target = { name: item.id, value: '' };
                    handleChange(e);
                  }}
                >
                  <CloseRounded />
                </IconButton>
              ),
            },
            select: {
              IconComponent: () => {
                if (item.id in data && data[item.id] !== '') {
                  return null;
                }
                return <ArrowDropDown />;
              },
            },
          }}
        />
      );
      break;
    case 'phone':
    case 'zipcode':
      // no-dd-sa:javascript-best-practices/no-case-declarations
      let inputMask = null;
      if (type === 'phone') {
        inputMask = phoneMask;
      } else if (type === 'zipcode') {
        inputMask = zipMask;
      }
      component = (
        <MaskedInput
          classes={classes}
          key={`${item.id}-select`}
          type={type}
          value={item.id in data ? data[item.id] : ''}
          autoFocus={item.autoFocus}
          id={item.id}
          name={item.id}
          disabled={'disabled' in item ? item.disabled : false}
          required={!('notRequired' in item) ? true : false}
          fullWidth
          className={!!classes[item.id] ? classes[item.id] : ''}
          error={!!formErrors[item.id]}
          helperText={formErrors[item.id]}
          variant={!!item.variant ? item.variant : 'outlined'}
          onChange={handleChange}
          mask={inputMask}
          onBlur={onBlur}
        />
      );
      break;
    default:
      break;
  }
  return (
    <>
      {label}
      {component}
    </>
  );
};

const handleFieldChange = (e, handleChange) => {
  const { name, value, type } = e.target;

  if (type === 'checkbox') {
    handleChange(e.target.name, e.target.checked);
  } else {
    e.preventDefault();
    handleChange(e.target.name, value);
  }
};

const GenericForm = ({
  handleChange,
  formErrors = {}, // Default value
  formFields = [], // Default value
  data = {}, // Default value
  classes = {}, // Default value
  onBlur = () => {
    /** empty */
  }, // Default function
  selectData = {}, // Default value
}) => {
  return (
    <form
      data-testid="client-info-form"
      onChange={(e) => {
        handleFieldChange(e, handleChange);
      }}
      className={classes.root}
    >
      <Grid container spacing={3}>
        {formFields.map((item) => {
          return (
            <Grid key={item.id} size={item.column ? item.column : 12}>
              {getField(
                classes,
                item,
                data,
                formErrors,
                (e) => {
                  handleFieldChange(e, handleChange);
                },
                onBlur,
                selectData,
              )}
            </Grid>
          );
        })}
      </Grid>
    </form>
  );
};

export default React.memo(GenericForm);
